/**
 * <bundle-builder> — quantity tiers + per-item colour picker.
 * Rendered by snippets/bundle-builder.liquid (main-product block "bundle_builder").
 *
 * Adds every selected colour as its own line to the cart in one request
 * (`/cart/add.js` with an `items` array), then opens the theme cart drawer.
 */
if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onStickyClick = this.onStickyClick.bind(this);
      }

      connectedCallback() {
        const readJson = (selector, fallback) => {
          try {
            const el = this.querySelector(selector);
            return el ? JSON.parse(el.textContent) : fallback;
          } catch (e) {
            return fallback;
          }
        };

        this.sectionId = this.dataset.section;
        this.tiers = readJson('[data-bb-tiers]', []).filter((t) => t.enabled);
        this.variants = readJson('[data-bb-variants]', []);
        this.swatches = readJson('[data-bb-swatches]', {});
        this.optionIndex = Number(this.dataset.colourOptionIndex || 0);
        this.hasColours = this.dataset.hasColours === 'true';
        this.maxQty = Number(this.dataset.maxQty || 1);
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.showSummary = this.dataset.showSummary === 'true';
        this.showButtonSummary = this.dataset.showButtonSummary === 'true';
        this.showUnitPrice = this.dataset.showUnitPrice === 'true';
        this.skipCart = this.dataset.skipCart === 'true';
        this.giftVariant = this.dataset.giftVariant || '';
        this.giftPerItem = Number(this.dataset.giftPerItem || 0);
        this.propertyName = (this.dataset.propertyName || '').trim();
        this.updateGallery = this.dataset.updateGallery === 'true';
        this.hijackSticky = this.dataset.hijackSticky !== 'false';

        this.currentVariant = this.getInitialVariant();
        const domValues = Array.from(this.querySelectorAll('[data-bb-base]')).map((b) => b.dataset.bbBase);
        this.colourValues = this.hasColours ? (domValues.length ? domValues : this.getColourValues()) : [];
        this.base = this.dataset.defaultColour || (this.colourValues[0] || '');
        if (this.hasColours && this.base && !this.isColourAvailable(this.base)) {
          const firstAvailable = this.colourValues.find((c) => this.isColourAvailable(c));
          if (firstAvailable) this.base = firstAvailable;
        }
        this.colours = Array(Math.max(this.maxQty, 1)).fill(this.base);

        const defaultTier = Number(this.dataset.defaultTier || 0);
        this.tier = this.tiers.find((t) => t.index === defaultTier) || this.tiers[0] || null;

        this.elements = {
          atc: this.querySelector('[data-bb-atc]'),
          atcLabel: this.querySelector('[data-bb-atc-label]'),
          spinner: this.querySelector('.bb__spinner'),
          error: this.querySelector('[data-bb-error]'),
          offers: Array.from(this.querySelectorAll('.bb__offer')),
          stickyButton: document.getElementById(`StickyAtcSubmitButton-${this.sectionId}`),
          stickyPrice: document.getElementById(`atc-price-${this.sectionId}`),
        };
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.paintSwatches();
        this.addEventListener('click', this.onClick);
        if (this.hijackSticky && this.elements.stickyButton) {
          this.elements.stickyButton.addEventListener('click', this.onStickyClick, true);
        }

        this.render();
        this.syncVariant(false);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.elements && this.elements.stickyButton) {
          this.elements.stickyButton.removeEventListener('click', this.onStickyClick, true);
        }
      }

      /* ---------- data helpers ---------- */

      getInitialVariant() {
        const urlVariant = new URLSearchParams(window.location.search).get('variant');
        if (urlVariant) {
          const found = this.variants.find((v) => String(v.id) === String(urlVariant));
          if (found) return found;
        }
        return this.variants.find((v) => v.available) || this.variants[0] || null;
      }

      getColourValues() {
        const seen = [];
        this.variants.forEach((v) => {
          const value = v.options[this.optionIndex];
          if (value !== undefined && !seen.includes(value)) seen.push(value);
        });
        return seen;
      }

      /** Variant matching `colour` while keeping the other options of the current variant. */
      findVariant(colour) {
        if (!this.hasColours) return this.currentVariant;
        const ref = this.currentVariant ? this.currentVariant.options : [];
        const match = this.variants.find((v) =>
          v.options.every((opt, i) => (i === this.optionIndex ? opt === colour : opt === ref[i]))
        );
        return match || this.variants.find((v) => v.options[this.optionIndex] === colour) || null;
      }

      isColourAvailable(colour) {
        const v = this.findVariant(colour);
        return !!(v && v.available);
      }

      swatchStyle(colour) {
        const mapped = this.swatches[colour];
        if (mapped) {
          if (/^(https?:)?\/\//.test(mapped) || mapped.startsWith('/')) return `url(${mapped})`;
          return mapped;
        }
        const variant = this.variants.find((v) => v.options[this.optionIndex] === colour);
        if (variant && variant.featured_image && variant.featured_image.src && this.swatches.__use_images) {
          return `url(${variant.featured_image.src})`;
        }
        return colour.toLowerCase().replace(/\s+/g, '');
      }

      paintSwatches() {
        this.querySelectorAll('[data-bb-swatch]').forEach((dot) => {
          const val = this.swatchStyle(dot.dataset.bbSwatch);
          if (val.startsWith('url(')) {
            dot.style.background = `${val} center / cover no-repeat`;
          } else {
            dot.style.setProperty('--swatch', val);
          }
        });
        this.querySelectorAll('.bb__swatch').forEach((btn) => {
          if (this.isColourAvailable(btn.dataset.bbColour)) btn.removeAttribute('data-unavailable');
          else btn.setAttribute('data-unavailable', 'true');
        });
      }

      /* ---------- pricing ---------- */

      unitPrice() {
        return this.currentVariant ? Number(this.currentVariant.price) : 0;
      }

      unitCompare(basis) {
        if (!this.currentVariant) return 0;
        const price = Number(this.currentVariant.price);
        const cmp = Number(this.currentVariant.compare_at_price || 0);
        return basis === 'compare_at' && cmp > price ? cmp : price;
      }

      /** Kaching quantity-break bar with the same quantity as this tier, when Kaching drives pricing. */
      kachingBar(tier) {
        if (this.dataset.pricingSource !== 'kaching' || !window.LuxeKaching) return null;
        if (this.kaching === undefined) {
          const K = window.LuxeKaching;
          const deal = K.getDeal(this.dataset.productId);
          const bars = K.getQuantityBars(deal);
          this.kaching = bars.length ? { deal, bars, config: K.getConfig() } : null;
        }
        if (!this.kaching) return null;
        return this.kaching.bars.find((b) => Number(b.quantity) === Number(tier.qty)) || null;
      }

      tierPrice(tier) {
        const bar = this.kachingBar(tier);
        if (bar) {
          return window.LuxeKaching.bundleTotal(bar, Array(tier.qty).fill(this.unitPrice()), this.kaching.deal, this.kaching.config);
        }
        if (tier.price !== null && tier.price !== undefined) return Math.max(0, Number(tier.price));
        const pctLeft = (100 - Number(tier.pct || 0)) / 100;
        const raw = Math.max(0, this.unitPrice() * tier.qty * pctLeft - Number(tier.fixed || 0));
        // Same rounding as Kaching "price rounding .99": whole units + .99, only on discounted tiers.
        if (this.dataset.priceRound === 'x99' && (Number(tier.pct) > 0 || Number(tier.fixed) > 0)) {
          return Math.floor(raw / 100) * 100 + 99;
        }
        return Math.round(raw);
      }

      tierCompare(tier) {
        if (this.kachingBar(tier)) return this.unitPrice() * tier.qty;
        if (tier.compare !== null && tier.compare !== undefined) return Number(tier.compare);
        return this.unitCompare(tier.basis) * tier.qty;
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const value = Number(cents) / 100;
        const fixed = (n, d, thousands = ',', decimal = '.') => {
          const parts = n.toFixed(d).split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts.length > 1 ? parts.join(decimal) : parts[0];
        };
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const type = match ? match[1] : 'amount';
        let out;
        switch (type) {
          case 'amount_no_decimals':
            out = fixed(value, 0);
            break;
          case 'amount_with_comma_separator':
            out = fixed(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            out = fixed(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            out = fixed(value, 2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            out = fixed(value, 0, ' ', '.');
            break;
          case 'amount_with_space_separator':
            out = fixed(value, 2, ' ', ',');
            break;
          default:
            out = fixed(value, 2);
        }
        return format.replace(/\{\{\s*\w+\s*\}\}/, out);
      }

      /* ---------- rendering ---------- */

      /** A tier with a fixed colour mix always sells exactly that mix. */
      hasMix(tier) {
        return !!(this.hasColours && tier && Array.isArray(tier.mix) && tier.mix.length);
      }

      activeColours() {
        if (this.hasMix(this.tier)) return this.tier.mix.slice();
        const qty = this.tier ? this.tier.qty : 1;
        return this.colours.slice(0, qty);
      }

      summary(separator) {
        if (!this.hasColours) return '';
        const sep = separator !== undefined ? separator : this.dataset.summarySeparator || ' • ';
        const chosen = this.activeColours();
        if (chosen.length === 1) return `1× ${chosen[0]}`;
        return this.colourValues
          .map((c) => ({ c, n: chosen.filter((x) => x === c).length }))
          .filter((x) => x.n)
          .map((x) => `${x.n}× ${x.c}`)
          .join(sep);
      }

      render() {
        // Global colour buttons
        this.querySelectorAll('[data-bb-base]').forEach((btn) => {
          btn.setAttribute('aria-pressed', String(btn.dataset.bbBase === this.base));
        });

        // Offers
        this.elements.offers.forEach((offer) => {
          const tierIndex = Number(offer.dataset.bbTier);
          const tier = this.tiers.find((t) => t.index === tierIndex);
          const active = !!(this.tier && this.tier.index === tierIndex);
          offer.classList.toggle('is-active', active);
          const head = offer.querySelector('[data-bb-select]');
          if (head) {
            head.setAttribute('aria-pressed', String(active));
            if (head.hasAttribute('aria-expanded')) head.setAttribute('aria-expanded', String(active));
          }
          const custom = offer.querySelector('[data-bb-custom]');
          if (custom) custom.hidden = !active;

          if (tier) {
            const price = this.tierPrice(tier);
            const compare = this.tierCompare(tier);
            const priceEl = offer.querySelector('[data-bb-price]');
            const compareEl = offer.querySelector('[data-bb-compare]');
            const unitEl = offer.querySelector('[data-bb-unit]');
            if (priceEl) priceEl.textContent = this.formatMoney(price);
            if (compareEl) {
              compareEl.textContent = this.formatMoney(compare);
              compareEl.hidden = !(compare > price);
            }
            if (unitEl) unitEl.textContent = this.formatMoney(Math.round(price / tier.qty));
          }

          // Per-light swatches
          offer.querySelectorAll('.bb__swatch').forEach((btn) => {
            const i = Number(btn.dataset.bbLight);
            btn.setAttribute('aria-pressed', String(this.colours[i] === btn.dataset.bbColour));
          });
          const summaryEl = offer.querySelector('[data-bb-summary]');
          if (summaryEl) summaryEl.textContent = active ? this.summary() : '';
        });

        // Button label
        const soldOut = this.hasColours
          ? this.activeColours().some((c) => !this.isColourAvailable(c))
          : !(this.currentVariant && this.currentVariant.available);
        const baseLabel = this.dataset.buttonLabel || 'Add to cart';
        let label = baseLabel;
        if (soldOut) {
          label = this.dataset.soldOutLabel || 'Sold out';
        } else if (this.showButtonSummary && this.hasColours && this.tier) {
          const format = this.dataset.buttonFormat || '[label] — [summary]';
          label = format.replace('[label]', baseLabel).replace('[summary]', this.summary(' + '));
        }
        if (this.elements.atcLabel) this.elements.atcLabel.textContent = label;
        if (this.elements.atc) this.elements.atc.setAttribute('aria-disabled', String(soldOut || !this.tier));

        // Sticky ATC price mirror
        if (this.elements.stickyPrice && this.tier) {
          const price = this.tierPrice(this.tier);
          const compare = this.tierCompare(this.tier);
          const showCompare = this.dataset.showComparePrice === 'true' && compare > price;
          this.elements.stickyPrice.innerHTML =
            `<span class="bb-sticky-price">${this.formatMoney(price)}</span>` +
            (showCompare ? ` <s class="bb-sticky-compare">${this.formatMoney(compare)}</s>` : '');
        }
      }

      /** Update gallery + URL to reflect the base colour's variant. */
      syncVariant(pushUrl) {
        if (!this.currentVariant) return;
        if (pushUrl && window.history && window.history.replaceState) {
          const url = `${this.dataset.productUrl}?variant=${this.currentVariant.id}`;
          window.history.replaceState({}, '', url);
        }
        if (!this.updateGallery) return;
        const media = this.currentVariant.featured_media;
        if (!media) return;
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          try {
            gallery.setActiveMedia(`${this.sectionId}-${media.id}`, true);
          } catch (e) {
            /* media not in gallery */
          }
        }
      }

      /* ---------- events ---------- */

      onClick(event) {
        const btn = event.target.closest('button');
        if (!btn || !this.contains(btn)) return;

        if (btn.dataset.bbBase !== undefined) {
          this.base = btn.dataset.bbBase;
          this.colours = this.colours.map(() => this.base);
          if (this.hasMix(this.tier)) {
            // The mix tier can't take a single colour; fall back to the default tier.
            const fallback = this.tiers.find((t) => t.index === Number(this.dataset.defaultTier) && !this.hasMix(t));
            this.tier = fallback || this.tiers.find((t) => !this.hasMix(t)) || this.tier;
          }
          const v = this.findVariant(this.base);
          if (v) this.currentVariant = v;
          this.hideError();
          this.render();
          this.syncVariant(true);
          return;
        }

        if (btn.dataset.bbSelect !== undefined) {
          const idx = Number(btn.dataset.bbSelect);
          this.tier = this.tiers.find((t) => t.index === idx) || this.tier;
          this.hideError();
          this.render();
          return;
        }

        if (btn.dataset.bbLight !== undefined) {
          if (btn.hasAttribute('data-unavailable')) return;
          this.colours[Number(btn.dataset.bbLight)] = btn.dataset.bbColour;
          this.hideError();
          this.render();
          return;
        }

        if (btn.hasAttribute('data-bb-atc')) {
          event.preventDefault();
          this.addToCart();
        }
      }

      onStickyClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addToCart();
      }

      /* ---------- cart ---------- */

      buildItems() {
        const items = [];
        const props = {};
        if (this.propertyName && this.tier) {
          const offer = this.elements.offers.find((o) => Number(o.dataset.bbTier) === this.tier.index);
          const label = offer ? [offer.dataset.bbLabel, offer.dataset.bbSublabel].filter(Boolean).join(' ') : '';
          if (label) props[this.propertyName] = label;
        }
        const withProps = (item) => (Object.keys(props).length ? { ...item, properties: props } : item);

        if (this.hasColours) {
          const counts = new Map();
          this.activeColours().forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
          counts.forEach((quantity, colour) => {
            const v = this.findVariant(colour);
            if (v) items.push(withProps({ id: v.id, quantity }));
          });
        } else if (this.currentVariant) {
          items.push(withProps({ id: this.currentVariant.id, quantity: this.tier ? this.tier.qty : 1 }));
        }

        if (this.giftVariant && this.giftPerItem > 0 && this.tier) {
          items.push(withProps({ id: Number(this.giftVariant), quantity: this.tier.qty * this.giftPerItem }));
        }
        return items;
      }

      setLoading(on) {
        const { atc, spinner, stickyButton } = this.elements;
        if (atc) {
          atc.classList.toggle('is-loading', on);
          atc.toggleAttribute('disabled', on);
        }
        if (spinner) spinner.hidden = !on;
        if (stickyButton) {
          stickyButton.classList.toggle('loading', on);
          const s = stickyButton.querySelector('.loading-overlay__spinner');
          if (s) s.classList.toggle('hidden', !on);
        }
      }

      showError(message) {
        if (!this.elements.error) return;
        this.elements.error.textContent = message;
        this.elements.error.hidden = false;
      }

      hideError() {
        if (this.elements.error) this.elements.error.hidden = true;
      }

      addToCart() {
        if (!this.tier || this.elements.atc.getAttribute('aria-disabled') === 'true' || this.elements.atc.disabled) return;
        const items = this.buildItems();
        if (!items.length) return;

        this.hideError();
        this.setLoading(true);

        const body = { items };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }

        const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || 'Could not add to cart.');
              return;
            }
            if (this.skipCart) {
              window.location = '/checkout';
              return;
            }
            if (!this.cart) {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }
            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: response });
            }
            // Cart drawer/notification expect a single-item payload shape; merge the first line in.
            const first = Array.isArray(response.items) ? response.items[0] : null;
            const parsed = first ? Object.assign({}, first, response) : response;
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(parsed);
          })
          .catch((err) => {
            console.error(err);
            this.showError((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
          })
          .finally(() => this.setLoading(false));
      }
    }
  );
}
